import React, { useRef, useState, useEffect } from 'react'
import { Canvas, useThree, invalidate } from '@react-three/fiber'
import { OrbitControls, Grid, Box, Cylinder, Sphere, Cone, TransformControls } from '@react-three/drei'
import { Mesh, Object3D, Vector3, Euler } from 'three'
import { SceneObject } from '../db/service'
import { TransformTooltip } from './TransformTooltip'

interface Scene3DProps {
  objects: SceneObject[]
  selectedTool: string
  selectedObjectId?: string | null
  onObjectSelect?: (id: string | null) => void
  onObjectTransform?: (id: string, position: [number, number, number], rotation: [number, number, number], scale: [number, number, number]) => void
  onTransformComplete?: (id: string, position: [number, number, number], rotation: [number, number, number], scale: [number, number, number]) => void
}

interface TooltipState {
  visible: boolean
  position: { x: number; y: number }
  mode: 'translate' | 'rotate' | 'scale' | null
  axis: 'x' | 'y' | 'z' | 'xy' | 'xz' | 'yz' | 'xyz' | null
  value: number | { x?: number; y?: number; z?: number }
}

// Selection outline component using edges
function SelectionOutline({ geometry, visible }: { geometry: any; visible: boolean }) {
  if (!visible) return null
  
  return (
    <lineSegments>
      <edgesGeometry args={[geometry]} />
      <lineBasicMaterial 
        color="#ff6600" 
        linewidth={3}
        depthTest={true}
        depthWrite={false}
        transparent={false}
        side={2}
      />
    </lineSegments>
  )
}

// Primitive components that can be selected and manipulated
function SceneCube({ object, onSelect, hierarchical }: { 
  object: SceneObject; 
  onSelect?: (id: string) => void;
  hierarchical?: boolean;
}) {
  const meshRef = useRef<Mesh>(null)
  const groupRef = useRef<any>(null)
  
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.userData.objectId = object.id
    }
  }, [object.id])
  
  if (hierarchical) {
    // For hierarchical rendering, don't wrap in group (parent group handles transform)
    return (
      <>
        <Box
          ref={meshRef}
          onClick={() => onSelect?.(object.id)}
          args={[
            1, 1, 1,
            object.properties?.cubeWidthSegments || 1,
            object.properties?.cubeHeightSegments || 1,
            object.properties?.cubeDepthSegments || 1
          ]}
        >
          <meshStandardMaterial color={object.selected ? "#60a5fa" : "#94a3b8"} />
        </Box>
        {object.selected && meshRef.current && (
          <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
        )}
      </>
    )
  }
  
  return (
    <group 
      ref={groupRef}
      position={object.position}
      rotation={object.rotation}
      scale={object.scale}
    >
      <Box
        ref={meshRef}
        onClick={() => onSelect?.(object.id)}
        args={[
          1, 1, 1,
          object.properties?.cubeWidthSegments || 1,
          object.properties?.cubeHeightSegments || 1,
          object.properties?.cubeDepthSegments || 1
        ]}
      >
        <meshStandardMaterial color={object.selected ? "#60a5fa" : "#94a3b8"} />
      </Box>
      {object.selected && meshRef.current && (
        <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
      )}
    </group>
  )
}

function SceneCylinder({ object, onSelect, hierarchical }: { 
  object: SceneObject; 
  onSelect?: (id: string) => void;
  hierarchical?: boolean;
}) {
  const meshRef = useRef<Mesh>(null)
  const groupRef = useRef<any>(null)
  
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.userData.objectId = object.id
    }
  }, [object.id])
  
  if (hierarchical) {
    return (
      <>
        <Cylinder
          ref={meshRef}
          onClick={() => onSelect?.(object.id)}
          args={[
            1, 1, 2, 
            object.properties?.cylinderRadialSegments || 16,
            object.properties?.cylinderHeightSegments || 1
          ]}
        >
          <meshStandardMaterial color={object.selected ? "#60a5fa" : "#94a3b8"} />
        </Cylinder>
        {object.selected && meshRef.current && (
          <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
        )}
      </>
    )
  }
  
  return (
    <group 
      ref={groupRef}
      position={object.position}
      rotation={object.rotation}
      scale={object.scale}
    >
      <Cylinder
        ref={meshRef}
        onClick={() => onSelect?.(object.id)}
        args={[
          1, 1, 2, 
          object.properties?.cylinderRadialSegments || 16,
          object.properties?.cylinderHeightSegments || 1
        ]}
      >
        <meshStandardMaterial color={object.selected ? "#60a5fa" : "#94a3b8"} />
      </Cylinder>
      {object.selected && meshRef.current && (
        <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
      )}
    </group>
  )
}

function SceneCone({ object, onSelect, hierarchical }: { 
  object: SceneObject; 
  onSelect?: (id: string) => void;
  hierarchical?: boolean;
}) {
  const meshRef = useRef<Mesh>(null)
  const groupRef = useRef<any>(null)
  
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.userData.objectId = object.id
    }
  }, [object.id])
  
  if (hierarchical) {
    return (
      <>
        <Cone
          ref={meshRef}
          onClick={() => onSelect?.(object.id)}
          args={[
            1, 2, 
            object.properties?.coneRadialSegments || 8,
            object.properties?.coneHeightSegments || 1
          ]}
        >
          <meshStandardMaterial color={object.selected ? "#60a5fa" : "#94a3b8"} />
        </Cone>
        {object.selected && meshRef.current && (
          <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
        )}
      </>
    )
  }
  
  return (
    <group 
      ref={groupRef}
      position={object.position}
      rotation={object.rotation}
      scale={object.scale}
    >
      <Cone
        ref={meshRef}
        onClick={() => onSelect?.(object.id)}
        args={[
          1, 2, 
          object.properties?.coneRadialSegments || 8,
          object.properties?.coneHeightSegments || 1
        ]}
      >
        <meshStandardMaterial color={object.selected ? "#60a5fa" : "#94a3b8"} />
      </Cone>
      {object.selected && meshRef.current && (
        <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
      )}
    </group>
  )
}

function SceneSphere({ object, onSelect, hierarchical }: { 
  object: SceneObject; 
  onSelect?: (id: string) => void;
  hierarchical?: boolean;
}) {
  const meshRef = useRef<Mesh>(null)
  const groupRef = useRef<any>(null)
  
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.userData.objectId = object.id
    }
  }, [object.id])
  
  if (hierarchical) {
    return (
      <>
        <Sphere
          ref={meshRef}
          onClick={() => onSelect?.(object.id)}
          args={[
            1, 
            object.properties?.sphereSegments || 16,
            object.properties?.sphereRings || 16
          ]}
        >
          <meshStandardMaterial color={object.selected ? "#60a5fa" : "#94a3b8"} />
        </Sphere>
        {object.selected && meshRef.current && (
          <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
        )}
      </>
    )
  }
  
  return (
    <group 
      ref={groupRef}
      position={object.position}
      rotation={object.rotation}
      scale={object.scale}
    >
      <Sphere
        ref={meshRef}
        onClick={() => onSelect?.(object.id)}
        args={[
          1, 
          object.properties?.sphereSegments || 16,
          object.properties?.sphereRings || 16
        ]}
      >
        <meshStandardMaterial color={object.selected ? "#60a5fa" : "#94a3b8"} />
      </Sphere>
      {object.selected && meshRef.current && (
        <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
      )}
    </group>
  )
}

function ScenePlane({ object, onSelect, hierarchical }: { 
  object: SceneObject; 
  onSelect?: (id: string) => void;
  hierarchical?: boolean;
}) {
  const meshRef = useRef<Mesh>(null)
  const groupRef = useRef<any>(null)
  
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.userData.objectId = object.id
    }
  }, [object.id])
  
  if (hierarchical) {
    return (
      <>
        <mesh
          ref={meshRef}
          onClick={() => onSelect?.(object.id)}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <planeGeometry args={[
            2, 2, 
            object.properties?.planeWidthSegments || 1,
            object.properties?.planeHeightSegments || 1
          ]} />
          <meshStandardMaterial 
            color={object.selected ? "#60a5fa" : "#94a3b8"} 
            side={2}
          />
        </mesh>
        {object.selected && meshRef.current && (
          <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
        )}
      </>
    )
  }
  
  return (
    <group 
      ref={groupRef}
      position={object.position}
      rotation={object.rotation}
      scale={object.scale}
    >
      <mesh
        ref={meshRef}
        onClick={() => onSelect?.(object.id)}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <planeGeometry args={[
            2, 2, 
            object.properties?.planeWidthSegments || 1,
            object.properties?.planeHeightSegments || 1
          ]} />
        <meshStandardMaterial 
          color={object.selected ? "#60a5fa" : "#94a3b8"} 
          side={2}
        />
      </mesh>
      {object.selected && meshRef.current && (
        <SelectionOutline geometry={meshRef.current.geometry} visible={true} />
      )}
    </group>
  )
}

// Helper function to check if an object is a descendant of another object
function isDescendantOf(objectId: string, ancestorId: string, objects: SceneObject[]): boolean {
  const obj = objects.find(o => o.id === objectId)
  if (!obj || !obj.parentId) return false
  
  if (obj.parentId === ancestorId) return true
  return isDescendantOf(obj.parentId, ancestorId, objects)
}

// Transform controls component
function TransformControlsManager({ 
  selectedObject, 
  selectedTool, 
  onTransform,
  onTransformComplete,
  onTooltipUpdate,
  isShiftPressed,
  selectedObjectId,
  onTransformingChange,
  objects
}: { 
  selectedObject: SceneObject | null
  selectedTool: string
  onTransform?: (id: string, position: [number, number, number], rotation: [number, number, number], scale: [number, number, number]) => void
  onTransformComplete?: (id: string, position: [number, number, number], rotation: [number, number, number], scale: [number, number, number]) => void
  onTooltipUpdate?: (tooltip: TooltipState) => void
  isShiftPressed?: boolean
  selectedObjectId?: string | null
  onTransformingChange?: (isTransforming: boolean) => void
  objects: SceneObject[]
}) {
  const transformRef = useRef<any>(null)
  const { scene, invalidate } = useThree()
  const [target, setTarget] = useState<Object3D | null>(null)
  const [initialTransform, setInitialTransform] = useState<{
    position: Vector3
    rotation: Euler
    scale: Vector3
  } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  
  // Throttle transform updates for state saves only
  const lastUpdateTime = useRef(0)
  const THROTTLE_MS = 8 // ~120fps - lighter throttling for smoother movement

  const handleTooltipUpdate = React.useCallback((tooltip: TooltipState) => {
    onTooltipUpdate?.(tooltip)
  }, [onTooltipUpdate])

  useEffect(() => {
    // Reset transform state when selection changes
    setIsDragging(false)
    setInitialTransform(null)
    
    // Only try to attach if we're using a transform tool
    if (selectedTool !== 'move' && selectedTool !== 'rotate' && selectedTool !== 'scale') {
      setTarget(null)
      return
    }
    
    const findAndAttachObject = () => {
      if (selectedObject && transformRef.current) {
        // Find the group in the scene by traversing
        const findGroupById = (obj: Object3D): Object3D | null => {
          if (obj.userData.objectId === selectedObject.id) {
            return obj
          }
          for (const child of obj.children) {
            const found = findGroupById(child)
            if (found) return found
          }
          return null
        }
        
        const group = findGroupById(scene)
        setTarget(group)
      } else if (!selectedObject) {
        setTarget(null)
      } else if (selectedTool === 'move' || selectedTool === 'rotate' || selectedTool === 'scale') {
        // Only retry if we're actually using a transform tool
        setTimeout(findAndAttachObject, 10)
      }
    }

    findAndAttachObject()
  }, [selectedObject?.id, selectedTool])  // Add selectedTool as dependency

  useEffect(() => {
    if (transformRef.current && target) {
      transformRef.current.attach(target)
      
      // Reset transform controls to ensure clean state
      transformRef.current.reset()
      
      const quantizeValue = (value: number, mode: 'translate' | 'rotate' | 'scale') => {
        if (!isShiftPressed) return value
        
        switch (mode) {
          case 'translate':
            return Math.round(value / 0.005) * 0.005 // 5mm quantization
          case 'rotate':
            return Math.round(value / (5 * Math.PI / 180)) * (5 * Math.PI / 180) // 5° quantization
          case 'scale':
            return Math.round(value / 0.1) * 0.1 // 0.1x quantization
          default:
            return value
        }
      }

      const handleDragStart = () => {
        console.log('Transform drag started')
        setIsDragging(true)
        onTransformingChange?.(true)
        if (target) {
          setInitialTransform({
            position: target.position.clone(),
            rotation: target.rotation.clone(),
            scale: target.scale.clone()
          })
        }
      }

      const handleDragEnd = () => {
        console.log('Transform drag ended')
        setIsDragging(false)
        onTransformingChange?.(false)
        setInitialTransform(null)
        handleTooltipUpdate({
          visible: false,
          position: { x: 0, y: 0 },
          mode: null,
          axis: null,
          value: 0
        })
        if (selectedObject && target) {
          // Validate that we're completing transform on the correct object or its hierarchy
          const isValidTarget = target.userData.objectId === selectedObject.id ||
                               isDescendantOf(target.userData.objectId, selectedObject.id, objects)
          
          if (!isValidTarget) {
            console.warn('Transform complete target mismatch! Expected:', selectedObject.id, 'or its children. Got:', target.userData.objectId)
            return
          }
          
          const pos = target.position.toArray() as [number, number, number]
          const rot = [target.rotation.x, target.rotation.y, target.rotation.z] as [number, number, number]
          const scale = target.scale.toArray() as [number, number, number]
          onTransformComplete?.(selectedObject.id, pos, rot, scale)
        }
      }

      const handleChange = () => {
        if (target && onTransform && selectedObject) {
          // Validate that we're transforming the correct object or its hierarchy
          const isValidTarget = target.userData.objectId === selectedObject.id ||
                               isDescendantOf(target.userData.objectId, selectedObject.id, objects)
          
          if (!isValidTarget) {
            console.warn('Transform target mismatch! Expected:', selectedObject.id, 'or its children. Got:', target.userData.objectId)
            return
          }
          
          // Light throttling for state updates only, not visual feedback
          const now = Date.now()
          const shouldUpdateState = now - lastUpdateTime.current >= THROTTLE_MS
          if (shouldUpdateState) {
          lastUpdateTime.current = now
          }
          
          const mode = selectedTool === 'move' ? 'translate' : selectedTool === 'rotate' ? 'rotate' : 'scale'
          
          // Always calculate tooltip values for smooth feedback
          if (isDragging && initialTransform) {
            let tooltipValue: number | { x?: number; y?: number; z?: number } = 0
            
            switch (mode) {
              case 'translate':
                const deltaPos = target.position.clone().sub(initialTransform.position)
                tooltipValue = {
                  x: deltaPos.x,
                  y: deltaPos.y,
                  z: deltaPos.z
                }
                break
              case 'rotate':
                const deltaRot = target.rotation.clone()
                deltaRot.x -= initialTransform.rotation.x
                deltaRot.y -= initialTransform.rotation.y
                deltaRot.z -= initialTransform.rotation.z
                tooltipValue = {
                  x: deltaRot.x,
                  y: deltaRot.y,
                  z: deltaRot.z
                }
                break
              case 'scale':
                const deltaScale = target.scale.clone().sub(initialTransform.scale)
                tooltipValue = {
                  x: deltaScale.x,
                  y: deltaScale.y,
                  z: deltaScale.z
                }
                break
            }

            handleTooltipUpdate({
              visible: true,
              position: { x: 0, y: 0 }, // Will be updated by mouse move
              mode,
              axis: 'xyz',
              value: tooltipValue
            })
          }

          // Only update state at throttled intervals to prevent jitter
          if (shouldUpdateState) {
            // Get current transform values for updating state
            let pos: [number, number, number] = [target.position.x, target.position.y, target.position.z]
            let rot: [number, number, number] = [target.rotation.x, target.rotation.y, target.rotation.z]
            let scale: [number, number, number] = [target.scale.x, target.scale.y, target.scale.z]
            
            // Apply quantization only to the values we send to state, not the visual object
            if (isShiftPressed) {
              switch (mode) {
                case 'translate':
                  pos = [
                    quantizeValue(pos[0], 'translate'),
                    quantizeValue(pos[1], 'translate'),
                    quantizeValue(pos[2], 'translate')
                  ]
                  break
                case 'rotate':
                  rot = [
                    quantizeValue(rot[0], 'rotate'),
                    quantizeValue(rot[1], 'rotate'),
                    quantizeValue(rot[2], 'rotate')
                  ]
                  break
                case 'scale':
                  scale = [
                    quantizeValue(scale[0], 'scale'),
                    quantizeValue(scale[1], 'scale'),
                    quantizeValue(scale[2], 'scale')
                  ]
                  break
              }
            }

            onTransform(selectedObject.id, pos, rot, scale)
          }
          
          invalidate() // Always invalidate for smooth visual feedback
        }
      }

      // Use the correct TransformControls events
      transformRef.current.addEventListener('change', handleChange)
      transformRef.current.addEventListener('objectChange', handleChange)
      transformRef.current.addEventListener('dragging-changed', (event: any) => {
        if (event.value) {
          handleDragStart()
        } else {
          handleDragEnd()
        }
      })
      
      return () => {
        if (transformRef.current) {
          transformRef.current.removeEventListener('change', handleChange)
          transformRef.current.removeEventListener('objectChange', handleChange)
          transformRef.current.removeEventListener('dragging-changed', (event: any) => {
            if (event.value) {
              handleDragStart()
            } else {
              handleDragEnd()
            }
          })
        }
      }
      
      // Cleanup: remove event listeners when effect re-runs or component unmounts
      return () => {
        if (transformRef.current) {
          transformRef.current.removeEventListener('change', handleChange)
          transformRef.current.removeEventListener('objectChange', handleChange) 
          transformRef.current.removeEventListener('dragging-changed', (event: any) => {
            if (event.value) {
              handleDragStart()
            } else {
              handleDragEnd()
            }
          })
          transformRef.current.detach()
        }
      }
    }
  }, [target?.uuid, selectedTool, isShiftPressed, selectedObject?.id])  // Use stable references

  if (!selectedObjectId || (selectedTool !== 'move' && selectedTool !== 'rotate' && selectedTool !== 'scale')) {
    return null
  }

  const mode = selectedTool === 'move' ? 'translate' : selectedTool === 'rotate' ? 'rotate' : 'scale'

  return (
    <TransformControls
      ref={transformRef}
      mode={mode}
      size={1}
      showX={true}
      showY={true}
      showZ={true}
      space="world"
      translationSnap={null}
      rotationSnap={null}
      scaleSnap={null}
    />
  )
}



// Component to invalidate frames when objects change
function FrameInvalidator({ objects, selectedTool }: { objects: SceneObject[]; selectedTool: string }) {
  useEffect(() => {
    invalidate()
  }, [objects, selectedTool])
  
  return null
}

// Component to handle clicking on empty space for deselection
function BackgroundClickHandler({ selectedTool, onObjectSelect }: { 
  selectedTool: string; 
  onObjectSelect?: (id: string | null) => void;
}) {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Only handle deselection when using select tool
      if (selectedTool !== 'select') return
      
      // Check if the click hit the canvas background (not an object)
      const canvas = event.target as HTMLCanvasElement
      if (canvas && canvas.tagName === 'CANVAS') {
        onObjectSelect?.(null)
      }
    }
    
    const canvas = document.querySelector('canvas')
    if (canvas) {
      canvas.addEventListener('click', handleClick)
      return () => canvas.removeEventListener('click', handleClick)
    }
  }, [selectedTool, onObjectSelect])
  
  return null
}

export function Scene3D({ objects, selectedTool, selectedObjectId, onObjectSelect, onObjectTransform, onTransformComplete }: Scene3DProps) {
  // Use selectedObjectId prop to determine the selected object, fallback to objects array
  const selectedObject = selectedObjectId 
    ? objects.find(obj => obj.id === selectedObjectId) || null
    : objects.find(obj => obj.selected) || null
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    position: { x: 0, y: 0 },
    mode: null,
    axis: null,
    value: 0
  })
  const [isShiftPressed, setIsShiftPressed] = useState(false)
  const [isTransforming, setIsTransforming] = useState(false)

  // Track shift key for quantization
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(true)
    }
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(false)
    }
    
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  // Track mouse position for tooltip
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setTooltip(prev => ({
        ...prev,
        position: { x: e.clientX, y: e.clientY }
      }))
    }
    
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  // Build hierarchical object tree for 3D rendering
  const buildObjectHierarchy = () => {
    const rootObjects = objects.filter(obj => !obj.parentId)
    
    const renderHierarchicalObject = (object: SceneObject): React.ReactNode => {
      const children = objects.filter(child => child.parentId === object.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
      
      return (
        <group
          key={object.id}
          position={object.position}
          rotation={object.rotation}
          scale={object.scale}
          userData={{ objectId: object.id }}
        >
          {/* Render the object itself */}
          {renderObjectPrimitive(object)}
          
          {/* Render children */}
          {children.map(child => renderHierarchicalObject(child))}
        </group>
      )
    }
    
    return rootObjects
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(obj => renderHierarchicalObject(obj))
  }

  // Render just the primitive without the group wrapper (for hierarchical rendering)
  const renderObjectPrimitive = (object: SceneObject) => {
    const props = { object, onSelect: onObjectSelect, hierarchical: true }
    
    switch (object.type) {
      case 'cube':
        return <SceneCube key={`primitive-${object.id}`} {...props} />
      case 'cylinder':
        return <SceneCylinder key={`primitive-${object.id}`} {...props} />
      case 'cone':
        return <SceneCone key={`primitive-${object.id}`} {...props} />
      case 'sphere':
        return <SceneSphere key={`primitive-${object.id}`} {...props} />
      case 'plane':
        return <ScenePlane key={`primitive-${object.id}`} {...props} />
      default:
        return null
    }
  }

  return (
    <>
    <Canvas
      camera={{ position: [5, 5, 5], fov: 60 }}
      style={{ background: 'linear-gradient(to bottom, #e2e8f0, #f8fafc)' }}
      gl={{ 
        antialias: true,
        alpha: false,
        powerPreference: "high-performance",
        stencil: false,
        depth: true
      }}
      dpr={[1, 2]}
      performance={{ min: 0.5 }}
frameloop="always"
    >
      {/* Lighting */}
      <ambientLight intensity={0.4} />
      <directionalLight 
        position={[10, 10, 5]} 
        intensity={1}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      <pointLight position={[-10, -10, -10]} intensity={0.3} />
      
      {/* Frame invalidation for demand-based rendering */}
      <FrameInvalidator objects={objects} selectedTool={selectedTool} />
      
      {/* Background click handler for deselection */}
      <BackgroundClickHandler selectedTool={selectedTool} onObjectSelect={onObjectSelect} />
      
      {/* Camera Controls */}
      <OrbitControls 
        enablePan={!isTransforming} 
        enableZoom={!isTransforming} 
        enableRotate={!isTransforming}
        dampingFactor={0.05}
        enableDamping
        makeDefault
        onChange={() => invalidate()}
      />
      
      {/* Fallback to working grid while debugging shader */}
      <Grid 
        position={[0, 0, 0]}
        args={[100, 100]}
        cellSize={1}
        cellThickness={0.5}
        cellColor="#94a3b8"
        sectionSize={5}
        sectionThickness={1.0}
        sectionColor="#64748b"
        fadeDistance={50}
        fadeStrength={1}
        infiniteGrid
        followCamera={false}
        side={2}
      />
      
      {/* Scene Objects */}
      {buildObjectHierarchy()}
      
      {/* Transform Controls */}
      <TransformControlsManager
        selectedObject={selectedObject}
        selectedTool={selectedTool}
        onTransform={onObjectTransform}
        onTransformComplete={onTransformComplete}
        onTooltipUpdate={setTooltip}
        isShiftPressed={isShiftPressed}
        selectedObjectId={selectedObjectId}
        onTransformingChange={setIsTransforming}
        objects={objects}
      />
      

      
      {/* Ground plane for shadows */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.003, 0]} receiveShadow>
        <planeGeometry args={[50, 50]} />
        <meshStandardMaterial 
          color="#f1f5f9" 
          transparent 
          opacity={0.5}
          side={2}
          depthWrite={false}
        />
      </mesh>
    </Canvas>
    
    {/* Transform Tooltip */}
    <TransformTooltip
      visible={tooltip.visible}
      position={tooltip.position}
      mode={tooltip.mode}
      axis={tooltip.axis}
      value={tooltip.value}
    />
  </>
  )
} 